/**
 * cl-core/stages — 단계 상수표 (지시서 §1-2 · §3). 순수 TS.
 *
 * ⚠️ 라벨은 `src/lib/apt/lifecycle-label.ts` 의 LIFECYCLE_LABEL 과 «같은 값» 이다.
 *    cl-core 는 Deno 에서도 읽혀 '@/' 별칭을 쓸 수 없어 표를 옮겨 적었고, 둘이 갈리지 않게
 *    단위 테스트(cl-core.test.ts)가 두 표를 키·값 단위로 대조한다.
 * ⚠️ 「통상 범위」는 이 표에만 있다(§1-2 채움 모드: 「범위는 cl-core 상수표만」).
 *    값은 국내 정비사업·분양 실무의 일반적 소요 기간(업계 통상치)이고 개별 현장의 예측이 아니다.
 *    문장에는 반드시 「통상」을 붙여 쓴다.
 */

export const STAGE_LABEL: Record<string, string> = {
  union_established: '조합설립',
  constructor_selected: '시공사 선정',
  plan_approved: '사업시행인가',
  mgmt_approved: '관리처분인가',
  construction: '착공',
  site_planning: '부지계획',
  pre_announcement: '분양 예고',
  model_house_open: '모델하우스',
  special_supply: '특별공급',
  subscription_open: '청약 진행',
  award_pending: '당첨자 발표 대기',
  award_announced: '당첨자 발표',
  contract_signing: '계약',
  contract: '계약',
  pre_move_in: '입주 예정',
  move_in_ready: '입주 예정',
  move_in: '입주',
  move_in_started: '입주중',
  post_move_in: '입주 후',
  active_trade: '실거래',
  resale: '실거래',
  unsold_active: '미분양',
  landmark_active: '랜드마크',
  redevelopment_active: '재개발',
};

/**
 * 정비 현장에서 lifecycle 라벨이 정비 어휘와 다른 단계(live1-d · sweep D).
 *   site_planning 은 정비 스테퍼 0번 「정비구역 지정」이다 — 배지 「부지계획」 vs 본문 「현재 정비구역 지정」이 갈렸다.
 *   pre_announcement·model_house_open 은 정비 7단계(일반분양) «안의 예고 국면» 이다 — 본문이 「현재 일반분양」이라 단정했다.
 */
const REDEV_STAGE_TEXT: Record<string, string> = {
  site_planning: '정비구역 지정',
  pre_announcement: '일반분양 예고',
  model_house_open: '일반분양 예고',
};

/**
 * 단계 칩 라벨(§1-2 「단계 칩은 lifecycle_stage 라벨 하나만」). 모르는 값은 null(칩 생략).
 * live1-d — kind 를 넘기면 배지·본문·FAQ 가 «같은 단계 문구»(stageDisplayLabel)를 쓴다. 생략 시 종전과 같다.
 */
export function stageChipLabel(stage: string | null | undefined, kind?: StepperKind, ctx?: StageContext): string | null {
  if (!stage) return null;
  const ph = presalePhase(stage, ctx);
  if (ph === 'announced') return ANNOUNCED_LABEL;
  if (ph === 'rcept_open') return kind === 'redev' ? '일반분양 청약' : '청약 진행';
  if (kind === 'redev' && stage === 'site_planning') return REDEV_STAGE_TEXT.site_planning;
  return STAGE_LABEL[stage] ?? null;
}

export interface StepDef {
  key: string;
  label: string;
  /** 이 단계에서 «다음 단계» 까지 통상 걸리는 기간. 마지막 단계는 null. */
  typicalToNext: string | null;
}

/** 정비사업(재개발·재건축) 8단계. */
export const REDEV_STEPS: readonly StepDef[] = [
  { key: 'zone_designated', label: '정비구역 지정', typicalToNext: '1~2년' },
  { key: 'union_established', label: '조합설립인가', typicalToNext: '6개월~1년' },
  { key: 'constructor_selected', label: '시공사 선정', typicalToNext: '1~2년' },
  { key: 'plan_approved', label: '사업시행인가', typicalToNext: '1~2년' },
  { key: 'mgmt_approved', label: '관리처분인가', typicalToNext: '1~2년' },
  { key: 'construction', label: '이주·철거·착공', typicalToNext: '6개월~1년' },
  { key: 'general_sale', label: '일반분양', typicalToNext: '2년 6개월~3년' },
  { key: 'move_in', label: '입주', typicalToNext: null },
];

/** 일반 분양 8단계. */
export const SALE_STEPS: readonly StepDef[] = [
  { key: 'site_planning', label: '부지계획·인허가', typicalToNext: '6개월~1년' },
  { key: 'pre_announcement', label: '분양 예고', typicalToNext: '1~3개월' },
  { key: 'subscription_open', label: '청약 접수', typicalToNext: '1~2주' },
  { key: 'award_announced', label: '당첨자 발표', typicalToNext: '2~4주' },
  { key: 'contract', label: '계약', typicalToNext: '수개월' },
  { key: 'construction', label: '공사', typicalToNext: '2년 6개월~3년' },
  { key: 'move_in', label: '입주 시작', typicalToNext: '2~3개월' },
  { key: 'post_move_in', label: '입주 완료', typicalToNext: null },
];

const REDEV_INDEX: Record<string, number> = {
  site_planning: 0,
  union_established: 1,
  constructor_selected: 2,
  plan_approved: 3,
  mgmt_approved: 4,
  construction: 5,
  pre_announcement: 6, model_house_open: 6, special_supply: 6, subscription_open: 6,
  award_pending: 6, award_announced: 6, contract_signing: 6, contract: 6, unsold_active: 6,
  pre_move_in: 7, move_in_ready: 7, move_in: 7, move_in_started: 7, post_move_in: 7,
};

const SALE_INDEX: Record<string, number> = {
  site_planning: 0,
  union_established: 0, constructor_selected: 0, plan_approved: 0, mgmt_approved: 0,
  pre_announcement: 1, model_house_open: 1,
  special_supply: 2, subscription_open: 2, award_pending: 2,
  award_announced: 3,
  contract_signing: 4, contract: 4, unsold_active: 4,
  construction: 5, pre_move_in: 5, move_in_ready: 5,
  move_in: 6, move_in_started: 6,
  post_move_in: 7, landmark_active: 7, active_trade: 7, resale: 7,
};

export type StepperKind = 'redev' | 'sale';

export interface StepperPosition {
  kind: StepperKind;
  steps: readonly StepDef[];
  /** 현재 단계 인덱스(0~7). 판정 불가면 null(스테퍼를 그리지 않는다). */
  current: number | null;
}

/** 정비 현장 판정: site_type='redevelopment' 이거나 정비 원장 연결이 있으면 정비. */
export function stepperKindOf(siteType: string | null | undefined, hasRedevSource = false): StepperKind {
  return siteType === 'redevelopment' || hasRedevSource ? 'redev' : 'sale';
}

/** 단계 판정 보조 맥락(live1-d). 생략하면 종전 판정과 같다. */
export interface StageContext {
  /** 입주(준공) 월이 이미 지났는가 — lib/apt/schedule moveInPast 한 곳의 판정을 넘긴다. */
  moveInPast?: boolean;
  /**
   * live2 c 지시 2 — 연결 모집공고(apt_sites.source_ids.subscription_id = apt_subscriptions.id) 국면. subscriptionPhase() 로 만든다.
   *   'announced' = 공고 났고 접수 전 · 'rcept_open' = 접수 기간 중. 생략·null 이면 종전과 같다.
   *   DB 단계(lifecycle_stage)는 그대로 두고 «표기» 만 바꾼다 — pre_announcement·model_house_open 일 때만 쓴다.
   */
  subPhase?: SubPhase | null;
}

export type SubPhase = 'announced' | 'rcept_open';

/** live2 c 지시 2 — 공고 났는데 접수 전인 분양 예고 현장의 단계 표기(종전 「분양 예고 2/8」 → 분양 스테퍼 3단). */
export const ANNOUNCED_LABEL = '모집공고·청약 예정';

const PRESALE_STAGES = new Set(['pre_announcement', 'model_house_open']);
/* kick-detail (2026-10-23) — 단계 칸이 공고 날짜보다 앞선 현장(subscription_open 인데 접수 시작 전 · 트리븐 센텀 D-3)도
 *   «공고 났고 접수 전» 으로 읽는다. 접수 중(rcept_open)은 subscription_open 의 종전 라벨(청약 진행·청약 접수)이 이미 맞다. */
const presalePhase = (stage: string | null | undefined, ctx?: StageContext): SubPhase | null =>
  stage && ctx?.subPhase && (PRESALE_STAGES.has(stage) || (stage === 'subscription_open' && ctx.subPhase === 'announced')) ? ctx.subPhase : null;

const ymdOf = (v: unknown): string | null => {
  const t = String(v ?? '').trim();
  const m8 = /^(\d{4})(\d{2})(\d{2})$/.exec(t);
  if (m8) return `${m8[1]}-${m8[2]}-${m8[3]}`;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

/**
 * live2 c 지시 2 — 현장에 «명시 연결» 된 모집공고 국면(순수). 이름 폴백 청약 행은 보지 않는다(연결 없으면 null).
 *   today < 접수 시작 → 'announced' · 시작 ≤ today ≤ 마감 → 'rcept_open' · 마감 지남·접수일 없음 → null(종전 표기).
 */
export function subscriptionPhase(
  sourceIds: { subscription_id?: unknown } | null | undefined,
  sub: { id?: unknown; rcept_bgnde?: unknown; rcept_endde?: unknown } | null | undefined,
  today: string,
): SubPhase | null {
  const sid = String(sourceIds?.subscription_id ?? '').trim();
  if (!sid || !sub || String(sub.id ?? '').trim() !== sid) return null;
  const bgn = ymdOf(sub.rcept_bgnde);
  if (!bgn) return null;
  const end = ymdOf(sub.rcept_endde) ?? bgn;
  if (today < bgn) return 'announced';
  return today <= end ? 'rcept_open' : null;
}

export function stepperPosition(stage: string | null | undefined, kind: StepperKind, ctx?: StageContext): StepperPosition {
  const steps = kind === 'redev' ? REDEV_STEPS : SALE_STEPS;
  const map = kind === 'redev' ? REDEV_INDEX : SALE_INDEX;
  let current = stage && map[stage] != null ? map[stage] : null;
  // live1-d(sweep G) — 준공 후 미분양: 입주월이 지났으면 「계약」(분양 5/8)이 아니라 입주 단계다.
  if (stage === 'unsold_active' && ctx?.moveInPast) current = kind === 'redev' ? 7 : 6;
  // live2 c 지시 2 — 분양 예고인데 연결 공고가 났다: 분양 스테퍼 3단(청약 접수 칸). 접수 전이면 그 칸 표기를 「모집공고·청약 예정」으로
  //   (접수일이 공고로 정해졌으니 「다음 단계까지 통상」 범위는 지운다). 정비 스테퍼는 이미 7단(일반분양) — 칸은 그대로.
  const ph = presalePhase(stage, ctx);
  if (ph && kind === 'sale') {
    const at = SALE_INDEX.subscription_open;
    const out = ph === 'announced' ? steps.map((x, i) => (i === at ? { ...x, label: ANNOUNCED_LABEL, typicalToNext: null } : x)) : steps;
    return { kind, steps: out, current: at };
  }
  return { kind, steps, current };
}

/**
 * seo_meta1 지시 2 — 상세 title 꼬리를 고르는 «단계 군» 4칸(2026-10-17 · 세션 A instr/cl/seo_meta1).
 *   'sale'     분양예정·청약       → 「분양정보·청약일정」
 *   'building' 공사 중·입주 전     → 「분양가·입주예정」
 *   'moved_in' 입주 후             → 「입주·시세」
 *   'redev'    정비(일반분양 전)   → 「진행현황·사업개요」
 * ⛔ 단계 목록을 새로 적지 않는다(HC-X-03 결 · 단일 출처) — 위 스테퍼 색인표(SALE_INDEX·REDEV_INDEX) 하나로 가른다:
 *   정비 현장 + 정비 스테퍼 0~5단(구역 지정~착공) = 'redev' · 그 밖은 분양 스테퍼 0~4단(부지계획~계약) 'sale' · 5단(공사) 'building' · 6~7단(입주~) 'moved_in'.
 *   스테퍼와 lifecycle-label(UPCOMING_SALE_STAGES·OPEN_SALE_STAGES·PIPELINE_STAGES)·title-tail(BUILT_STAGES)의 정합은 cl-core.test 가 잠근다.
 * 단계가 비었거나 표에 없으면: 정비 → 'redev' · 기존 아파트(trade·landmark) → 'moved_in' · 그 밖 null(호출부 기본 'sale').
 */
export type TitleStageGroup = 'sale' | 'building' | 'moved_in' | 'redev';

export function titleStageGroupOf(stage: string | null | undefined, siteType?: string | null): TitleStageGroup | null {
  const st = String(stage ?? '');
  if (siteType === 'redevelopment') {
    const r = REDEV_INDEX[st];
    if (r == null && SALE_INDEX[st] == null) return 'redev';
    if (r != null && r <= REDEV_INDEX.construction) return 'redev';
  }
  const i = SALE_INDEX[st];
  if (i == null) return siteType === 'trade' || siteType === 'landmark' ? 'moved_in' : null;
  if (i < SALE_INDEX.construction) return 'sale';
  if (i === SALE_INDEX.construction) return 'building';
  return 'moved_in';
}

export interface NextStageInfo {
  current: StepDef;
  next: StepDef | null;
  /** 현재 → 다음 통상 기간(상수표). 다음이 없으면 null. */
  typicalRange: string | null;
}

/** 정비 「일반분양 예고」 국면의 다음 단계 — 분양 표의 청약 접수와 같은 통상 범위(1~3개월). */
const REDEV_PRESALE_NEXT: StepDef = { key: 'subscription_open', label: '일반분양 청약', typicalToNext: null };

/**
 * 단계 «문구» 한 벌(live1-d · sweep D·G) — 배지·리드·FAQ·스테퍼 메모가 같은 필드(lifecycle_stage)에서 같은 말을 한다.
 *   · 정비 + site_planning → 「정비구역 지정」(배지도 stageChipLabel(stage,'redev') 로 같은 값)
 *   · 정비 + 분양 예고 국면 → 「일반분양 예고」, 다음 = 「일반분양 청약」(통상 1~3개월) — 종전 「현재 일반분양 · 다음 입주 2년 6개월~3년」
 *   · 미분양(unsold_active) → 현재 「미분양」(입주월이 지났으면 「준공 후 미분양」), 다음 단계 문장 없음 —
 *     종전 「현재 계약입니다. 다음 단계는 공사이며 통상 수개월」은 준공 후 미분양에 틀린 안내였다.
 */
export function stageDisplayLabel(stage: string | null | undefined, kind: StepperKind, ctx?: StageContext): string | null {
  const info = nextStageInfo(stage, kind, ctx);
  return info ? info.current.label : null;
}

/** 현재·다음 단계와 통상 범위. 판정 불가면 null(문장을 쓰지 않는다). */
const STAGE_LABEL_AWARD_PENDING = '당첨자 발표 대기';

export function nextStageInfo(stage: string | null | undefined, kind: StepperKind, ctx?: StageContext): NextStageInfo | null {
  const pos = stepperPosition(stage, kind, ctx);
  if (pos.current == null) return null;
  const step = pos.steps[pos.current];
  if (stage === 'unsold_active') {
    return { current: { ...step, label: ctx?.moveInPast ? '준공 후 미분양' : '미분양', typicalToNext: null }, next: null, typicalRange: null };
  }
  const ph = presalePhase(stage, ctx);
  if (ph === 'announced') {
    // live2 c 지시 2 — 공고 났고 접수 전: 다음 = 청약 접수(날짜가 공고로 정해졌다 — 「통상」 범위 문장은 쓰지 않는다).
    return { current: { ...step, label: ANNOUNCED_LABEL, typicalToNext: null }, next: kind === 'redev' ? REDEV_PRESALE_NEXT : { key: 'subscription_open', label: '청약 접수', typicalToNext: null }, typicalRange: null };
  }
  if (ph === 'rcept_open' && kind === 'redev') {
    return { current: { ...step, label: '일반분양 청약', typicalToNext: null }, next: null, typicalRange: null };
  }
  if (kind === 'redev' && stage && REDEV_STAGE_TEXT[stage] && stage !== 'site_planning') {
    return { current: { ...step, label: REDEV_STAGE_TEXT[stage], typicalToNext: '1~3개월' }, next: REDEV_PRESALE_NEXT, typicalRange: '1~3개월' };
  }
  // ui-1101 — 접수 끝·발표 전(award_pending)은 「현재 청약 접수」 가 아니다(머리 칩 「당첨자 발표 대기」 와 어긋났다). 칸 위치는 그대로, 이름만.
  const current = stage === 'award_pending' && kind === 'sale' ? { ...step, label: STAGE_LABEL_AWARD_PENDING } : step;
  const next = pos.current + 1 < pos.steps.length ? pos.steps[pos.current + 1] : null;
  return { current, next, typicalRange: next ? current.typicalToNext : null };
}

/** 정비사업 유형어. 원장 project_type 에 재건축이 보이면 재건축, 아니면 재개발. */
export function redevTypeWord(projectType: string | null | undefined, name?: string | null): '재건축' | '재개발' {
  const s = `${projectType ?? ''} ${name ?? ''}`;
  return /재건축/.test(s) ? '재건축' : '재개발';
}
